import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFilePdf, 
  faFileWord, 
  faFileExcel, 
  faFileAlt,
  faFilePowerpoint,
  faFileImage,
  faFileArchive,
  faEnvelope
} from "@fortawesome/free-solid-svg-icons";

/**
 * Maps a placeholder token to its runtime value.
 */
export const resolveToken = (token, userEmail, crmId) => {
  if (!token) return "";
  return token
    .replace(/{{userEmail}}/g, userEmail || "")
    .replace(/{{crmId}}/g, crmId || "")
    .replace(/{{LoanId}}/g, crmId || "");
};

/**
 * Formats a value as GBP currency.
 */
export const formatCurrency = (value) => {
  if (value == null || value === "") return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(value);
};

/**
 * Formats a date string to DD MMM YYYY format.
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

/**
 * Checks if a task is overdue (past due date and not closed/approved).
 */
export const isOverdue = (dateStr, status) => {
  if (
    !dateStr ||
    status?.toLowerCase() === "closed" ||
    status?.toLowerCase() === "approved" ||
    status?.toLowerCase() === "rejected"
  )
    return false;
  try {
    const dueDate = new Date(dateStr);
    const today = new Date();
    // Normalize to the start of today to ensure tasks due today are not marked as overdue
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  } catch {
    return false;
  }
};

/**
 * Standard sorting logic for grid.
 */
export const descendingComparator = (a, b, orderBy) => {
  const valA = a[orderBy] ?? "";
  const valB = b[orderBy] ?? "";
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
};

export const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const stableSort = (array, comparator) => {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
};

/**
 * Filters rows based on a search term across multiple fields.
 */
export const filterRows = (rows, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) return rows;
  const term = searchTerm.toLowerCase().trim();
  return rows.filter((row) => {
    return (
      (row.taskNumber || "").toLowerCase().includes(term) ||
      (row.title || "").toLowerCase().includes(term) ||
      (row.description || "").toLowerCase().includes(term) ||
      (row.taskType || "").toLowerCase().includes(term) ||
      (row.status || "").toLowerCase().includes(term) ||
      (row.assignedTo || "").toLowerCase().includes(term) ||
      (row.stage || "").toLowerCase().includes(term) ||
      (row.createdBy || "").toLowerCase().includes(term)
    );
  });
};

/**
 * Returns extension-specific file icons using FontAwesome.
 * Supports banking-standard file types.
 */
export const getFileIcon = (fileName = "") => {
  const ext = fileName.split(".").pop().toLowerCase();
  switch (ext) {
    case "pdf":
      return <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ef4444", fontSize: "1.125rem" }} />;
    case "doc":
    case "docx":
      return <FontAwesomeIcon icon={faFileWord} style={{ color: "#2b579a", fontSize: "1.125rem" }} />;
    case "xls":
    case "xlsx":
    case "xlsm":
    case "csv":
      return <FontAwesomeIcon icon={faFileExcel} style={{ color: "#1e7145", fontSize: "1.125rem" }} />;
    case "ppt":
    case "pptx":
      return <FontAwesomeIcon icon={faFilePowerpoint} style={{ color: "#d24726", fontSize: "1.125rem" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return <FontAwesomeIcon icon={faFileImage} style={{ color: "#8b5cf6", fontSize: "1.125rem" }} />;
    case "zip":
    case "rar":
    case "7z":
      return <FontAwesomeIcon icon={faFileArchive} style={{ color: "#f59e0b", fontSize: "1.125rem" }} />;
    case "msg":
    case "eml":
      return <FontAwesomeIcon icon={faEnvelope} style={{ color: "#0ea5e9", fontSize: "1.125rem" }} />;
    default:
      return <FontAwesomeIcon icon={faFileAlt} style={{ color: "#64748b", fontSize: "1.125rem" }} />;
  }
};

export const TASK_API_BASE = "/TaskManagement";

export const STATUS_DISPLAY = {
  Open: "Open",
  InProgress: "In Progress",
  Closed: "Closed",
  Approved: "Approved",
  Rejected: "Rejected",
};

export const STATUS_PRIORITY = {
  Open: 1,
  InProgress: 2,
  Approved: 3,
  Closed: 3,
  Rejected: 4,
};

export const sortTasksByStatus = (tasks) => {
  if (!Array.isArray(tasks)) return [];
  return [...tasks].sort((a, b) => {
    const priorityA = STATUS_PRIORITY[a.status] || 99;
    const priorityB = STATUS_PRIORITY[b.status] || 99;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    // Secondary sort by created date if statuses are same
    return new Date(b.createdOn || 0) - new Date(a.createdOn || 0);
  });
};

export const TASK_TYPE_DISPLAY = {
  Normal: "Normal",
  ApprovalBased: "Approval",
};

export const getInitials = (value) =>
  (value || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((i) => i[0])
    .join("")
    .toUpperCase();

export const toISODateString = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const DATE_RANGE_PRESETS = [
  { key: "lastWeek", label: "Last Week" },
  { key: "lastMonth", label: "Last Month" },
  { key: "thisMonth", label: "This Month" },
  { key: "next30Days", label: "Next 30 Days" },
  { key: "nextMonth", label: "Next Month" },
];

export const getPresetDateRange = (key) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (key) {
    case "lastWeek": {
      const start = new Date(today);
      start.setDate(today.getDate() - 7);
      return { start: toISODateString(start), end: toISODateString(today) };
    }
    case "lastMonth": {
      const start = new Date(today);
      start.setMonth(today.getMonth() - 1);
      return { start: toISODateString(start), end: toISODateString(today) };
    }
    case "thisMonth": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: toISODateString(start), end: toISODateString(end) };
    }
    case "next30Days": {
      const end = new Date(today);
      end.setDate(today.getDate() + 30);
      return { start: toISODateString(today), end: toISODateString(end) };
    }
    case "nextMonth": {
      const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { start: toISODateString(start), end: toISODateString(end) };
    }
    default:
      return { start: "", end: "" };
  }
};
